import React, { useState, useEffect } from 'react';
import * as Yup from 'yup';
import {
   Grid,
   GridItem,
   useColorModeValue,
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalCloseButton,
   ModalBody,
   ModalFooter,
   Button,
   useToast,
} from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { renderFormControl } from 'utils/DynamicFormData';
import {
   updateSalesRepresentative,
   addSalesRepresentative,
   getSalesRepresentative,
} from '../../../../../redux/actions/salesRepresentative';
import '../../../companyProfile/css.css';
import { DynamicInput } from 'components/UIcomponents/Dynamicinput';
import { renderDynamicDropdown } from 'components/UIcomponents/DynamicDropdown';
// Yup Validation Schema
const validationSchema = Yup.object().shape({
   name: Yup.string().required('Name is required').min(1, 'Name must be at least 1 characters long'),
   mobile: Yup.string()
      .matches(/^[0-9+\-(). ]*$/, 'Mobile number can only contain numbers, spaces, and special characters (+, -, (, ))')
      .required('Mobile number is required')
      .min(10, 'Mobile number must be at least 10 characters long')
      .max(10, 'Mobile number cannot exceed 10 characters'),
});

const SalesRepresentativeMasterForm = ({ isOpen, onClose, isEditMode = false, demoname, currentRow = null }) => {
   const [row, setRow] = useState({
      name: '',
      mobile: '',
   });

   const [errors, setErrors] = useState({});
   // const [activeStep, setActiveStep] = useState(0);
   const toast = useToast();
   const textColor = useColorModeValue('#9E9E9E', 'white');
   const dispatch = useDispatch();

   // Function to clear form data
   const clearFormData = () => {
      setRow({
         name: '',
         mobile: '',
      });
      setErrors({});
   };

   // Populate form with current row data when in edit mode
   useEffect(() => {
      if (!isEditMode) {
         clearFormData(); // Clear data when adding a new item
      } else if (isEditMode && currentRow) {
         setRow({
            name: currentRow.name || '',
            mobile: currentRow.mobile || '',
         });
      }
   }, [isEditMode, currentRow]);

   const validateStep = async () => {
      try {
         // Validate the whole row object instead of individual fields
         await validationSchema.validate(row, { abortEarly: false });

         setErrors({}); // Clear previous errors
         return true;
      } catch (err) {
         if (err instanceof Yup.ValidationError) {
            const errorObj = {};
            err.inner.forEach((error) => {
               errorObj[error.path] = error.message;
            });
            setErrors(errorObj);
            return false;
         }
         return false;
      }
   };

   const handleInputChange = (field, value) => {
      setRow((prev) => ({
         ...prev,
         [field]: value,
      }));

      // Clear the specific field error when user starts typing
      setErrors((prevErrors) => {
         const newErrors = { ...prevErrors };
         if (newErrors[field]) {
            delete newErrors[field];
         }
         return newErrors;
      });
   };

   const onFormSubmit = async () => {
      const processedData = {
         ...row,
      };

      try {
         if (isEditMode && currentRow && currentRow.id) {
            // Update existing item
            processedData.id = currentRow.id;
            await dispatch(updateSalesRepresentative(processedData));
         } else {
            // Add new item
            await dispatch(addSalesRepresentative(processedData));
         }
         dispatch(getSalesRepresentative());
         // Close the modal
         onClose();
      } catch (error) {
         toast({
            title: 'Error',
            description: error.message || 'An error occurred while saving the Sales Representative.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
      }
   };

   const handleSubmit = async () => {
      const isValid = await validateStep();
      if (isValid) {
         onFormSubmit(); // Call the form submit logic if validation is successful
      } else {
         toast({
            title: 'Validation Error',
            description: 'Please correct the errors before saving.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
      }
   };

   const handleCansel = async () => {
      onClose();
      if (!isEditMode) {
         clearFormData();
      }
   };

   useEffect(() => {
      if (demoname && row.name === '') {
         setRow((prev) => ({ ...prev, name: demoname }));
      }
      demoname = '';
   }, [demoname, row.name]);

   return (
      <Modal isOpen={isOpen} onClose={handleCansel} size="4xl">
         <ModalOverlay />
         <ModalContent
            bg={useColorModeValue('white', '#2d3748')}
            color={useColorModeValue('gray.800', 'white')}
            borderRadius="20px"
            p={0}
            mx="15px"
            mt={{ base: '35%', md: '25%', xl: '15%' }}
            boxShadow="lg">
            <ModalHeader fontSize="20px" fontWeight="600" mb={{ base: 0, xl: 4 }}>
               {isEditMode ? 'Edit Sales Representative' : 'Add New Sales Representative'}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
               {/* {activeStep === 0 && ( */}
               <Grid templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)', xl: 'repeat(2, 1fr)' }} gap={2}>
                  <GridItem className="seperator-width">
                     {DynamicInput(
                        'name',
                        'Name',
                        {
                           value: row.name,
                           onChange: (e) => handleInputChange('name', e.target.value),
                        },
                        errors,
                        textColor,
                        'text',
                        true, // optional: mark as required
                     )}
                  </GridItem>
                  <GridItem className="seperator-width">
                     {DynamicInput(
                        'mobile',
                        'Mobile',
                        {
                           value: row.mobile,
                           onChange: (e) => handleInputChange('mobile', e.target.value),
                        },
                        errors,
                        textColor,
                        'number',
                        true, // optional: mark as required
                     )}
                  </GridItem>
               </Grid>
            </ModalBody>
            <ModalFooter>
               <Button onClick={handleSubmit} colorScheme="green" ml={3}>
                  Save
               </Button>

               <Button variant="ghost" onClick={handleCansel} ml={3}>
                  Cancel
               </Button>
            </ModalFooter>
         </ModalContent>
      </Modal>
   );
};

export default SalesRepresentativeMasterForm;
